import { Box, Divider } from "@mui/material";
import type { DateTime } from "luxon";
import { type ComponentProps, type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { useRevealWage } from "../../hooks/useRevealWage";
import { STAFF_PICKER } from "../../theme/tokens";
import { formatPredictedCost } from "../../utils/costPrediction";
import type { Nurse, NurseData, NurseFullRole } from "../../utils/models";
import { NursePanelHeader } from "../../utils/panelComponents";
import { getShiftIndexForWindow } from "../../utils/shiftOptionWindows";
import type { NursesMatrixSelectionMode } from "../NursesMatrixGrid/consts";
import { AddStaffDialogs } from "./AddStaffDialogs";
import { AddStaffFooter } from "./AddStaffFooter";
import { ADD_STAFF_ANALYTICS_SOURCE } from "./addStaffPublish";
import {
	buildAddStaffSummary,
	canRetargetPanelDate,
	confirmButtonLabel,
	formatShiftDate,
	type LoadedDay,
	type ShiftDetail,
	shiftDetailToAssignment,
	sumSelectedCost,
} from "./addStaffUtils";
import { ShiftStep } from "./ShiftStep";
import { StaffStep } from "./StaffStep";
import { type AddStaffDate, useAddStaffDate } from "./useAddStaffDate";
import { useAddStaffFilters } from "./useAddStaffFilters";
import { useAddStaffList } from "./useAddStaffList";
import { useAddStaffSelection } from "./useAddStaffSelection";
import { useAddStaffShift } from "./useAddStaffShift";
import { type AddStaffSubmit, useAddStaffSubmit } from "./useAddStaffSubmit";

/** A short screen tightens the header and the body (see `STAFF_PICKER.COMPACT_VIEWPORT_QUERY`), so the staff list keeps its rows. */
const HEADER_SX = { px: 3, pt: 2.5, pb: 1.25, [STAFF_PICKER.COMPACT_VIEWPORT_QUERY]: { pt: 1.5, pb: 0.5 } } as const;
const BODY_SX = {
	flex: 1,
	minHeight: 0,
	// Scrolls only when step 1 plus the list's floor (`STAFF_PICKER.LIST_MIN_HEIGHT`) outgrow the panel, so the
	// list never collapses; the header and the footer stay in place.
	overflowY: "auto",
	display: "flex",
	flexDirection: "column",
	gap: 1.75,
	px: 3,
	pb: 2,
	[STAFF_PICKER.COMPACT_VIEWPORT_QUERY]: { gap: 1, pb: 1.5 },
} as const;

export interface AddNurseProps {
	setIsAdd: (value: boolean) => void;
	onNurseAddedAvoidActualAdding?: (selectedNurseId: number) => void;
	dayNo: number;
	shiftNo: number;
	// Per-cell data-week index. Required when the cell may be a visible-window spillover
	// (Wed-view's Sun/Mon/Tue, etc.) where the global WeekIndexProvider value points at
	// the previous data-week. Defaults to the global context for non-rotated callers.
	weekIndex?: number;
	rolesToDisplay: NurseFullRole[];
	onNurseSelected?: (nurse: Nurse, selectedDate: DateTime | null) => void;
	hideRoomSelect?: boolean;
	isCharged?: boolean;
	onClose?: () => void;
	onShiftChange?: (shift: number) => void;
	showActual?: boolean;
	setOpen: (open: boolean) => void;
	lockedTimes?: {
		startTime: DateTime;
		endTime: DateTime;
	};
	nurseIdsToIgnore?: number[];
	defaultRoom?: string;
	requestJobId?: string;
	skipRedundantOpenShiftSuggestion?: boolean;
	/**
	 * One nurse (radio) or several (checkboxes). Callers whose callback takes exactly one nurse — a
	 * recommendation, a request fill, the hospital `onNurseSelected` flow — must stay "single".
	 */
	selectionMode?: NursesMatrixSelectionMode;
	/** False opens on "Pick a shift first" (day-level entry points); true preselects the cell's default hours. */
	initialShiftSelected?: boolean;
	/** The job the shift cards and the list start on (falls back to the request's job, then the first enabled job). */
	defaultJobId?: string;
	/**
	 * Let the header's date become a picker that re-targets the whole tab to another loaded day. Only for
	 * callers that publish to whatever day the panel is on — a recommendation's or a hospital hand-off's
	 * callback lands on its own day. Never offered for a request's locked shift or for worked (actual) hours.
	 */
	allowDateChange?: boolean;
	/** Told about every date picked in the header, so the caller (Staffing Changes' other tabs) follows it. */
	onSelectedDateChange?: (date: DateTime) => void;
}

/** The props with every optional behaviour switch resolved, so the hooks below read plain values. */
const resolveAddNurseProps = (props: AddNurseProps) => ({
	...props,
	// DEFAULT: the opt-in flags below are off unless a caller turns them on — as they always were.
	hideRoomSelect: props.hideRoomSelect ?? false,
	isCharged: props.isCharged ?? false,
	showActual: props.showActual ?? false,
	skipRedundantOpenShiftSuggestion: props.skipRedundantOpenShiftSuggestion ?? false,
	// DEFAULT: one nurse, like every Add Nurse panel before multi-add; Staffing Changes opts into "multiple".
	selectionMode: props.selectionMode ?? "single",
	// DEFAULT: preselect the cell's default hours — the behaviour every caller had before step 1 existed.
	initialShiftSelected: props.initialShiftSelected ?? true,
	// DEFAULT: the panel stays on the caller's day, as it always did, unless the caller opts in.
	allowDateChange: props.allowDateChange ?? false,
});

type ResolvedAddNurseProps = ReturnType<typeof resolveAddNurseProps>;

/** Step 1 + step 2 state: the shift, the filters, who is listed for it and who is picked — all for one day. */
const useAddStaffSteps = (props: ResolvedAddNurseProps, { weekIndex, dayNo }: LoadedDay) => {
	const { rolesToDisplay, requestJobId } = props;
	const filters = useAddStaffFilters(weekIndex, props.defaultRoom);
	const shift = useAddStaffShift({
		dayNo,
		weekIndex,
		initialShiftNo: props.shiftNo,
		rolesToDisplay,
		lockedTimes: props.lockedTimes,
		defaultJobId: props.defaultJobId,
		requestJobId,
		initialShiftSelected: props.initialShiftSelected,
		selectedRoom: filters.selectedRoom,
		onShiftChange: props.onShiftChange,
	});
	const list = useAddStaffList({
		shift,
		weekIndex,
		dayNo,
		rolesToDisplay,
		requestJobId,
		contractTypes: filters.contractTypes,
		chargeOnly: props.isCharged,
		showActual: props.showActual,
		nurseIdsToIgnore: props.nurseIdsToIgnore,
	});
	const selection = useAddStaffSelection({ weekIndex, dayNo, shiftNo: shift.shiftNo });
	return { filters, shift, list, selection };
};

type AddStaffSteps = ReturnType<typeof useAddStaffSteps>;

/** The panel's whole model: both steps, the "Shift details" choice and the confirm flow. */
const useAddStaffPanel = (rawProps: AddNurseProps) => {
	const props = resolveAddNurseProps(rawProps);
	const { weekIndex: ctxWeekIndex } = useWeekIndex();
	const date = useAddStaffDate({
		initial: { weekIndex: props.weekIndex ?? ctxWeekIndex, dayNo: props.dayNo },
		enabled: canRetargetPanelDate(props),
		onSelectedDateChange: props.onSelectedDateChange,
		analyticsSource: ADD_STAFF_ANALYTICS_SOURCE,
	});
	const { weekIndex, dayNo } = date;
	const steps = useAddStaffSteps(props, date);
	const [detail, setDetail] = useState<ShiftDetail>(props.isCharged ? "charge" : "none");
	const assignment = useMemo(() => shiftDetailToAssignment(detail), [detail]);
	const { filters, shift, list, selection } = steps;
	const close = () => {
		props.setOpen(false);
		props.onClose?.();
	};
	const submit = useAddStaffSubmit({
		selectedNurses: selection.selectedNurses,
		rows: list.rows,
		arePredictionsPending: list.arePredictionsPending,
		window: shift.window,
		staffingDate: shift.staffingDate,
		shiftNo: shift.shiftNo,
		dayNo,
		weekIndex,
		room: filters.selectedRoom,
		jobId: list.targetJobId,
		assignment,
		showActual: props.showActual,
		hideRoomSelect: props.hideRoomSelect,
		onNurseSelected: props.onNurseSelected,
		onNurseAddedAvoidActualAdding: props.onNurseAddedAvoidActualAdding,
		setIsAdd: props.setIsAdd,
		close,
		skipRedundantOpenShiftSuggestion: props.skipRedundantOpenShiftSuggestion,
	});
	const footer = useFooterProps({ props, steps, detail, setDetail, submit, close });
	return { props, date, steps, submit, footer };
};

type FooterArgs = {
	props: ResolvedAddNurseProps;
	steps: AddStaffSteps;
	detail: ShiftDetail;
	setDetail: (detail: ShiftDetail) => void;
	submit: AddStaffSubmit;
	close: () => void;
};

/** The footer's read-back and buttons — `null` until a shift is picked (there is nothing to add yet). */
const useFooterProps = ({ props, steps, detail, setDetail, submit, close }: FooterArgs): ComponentProps<typeof AddStaffFooter> | null => {
	const { t } = useTranslation();
	const formatPersonName = useFormatPersonName();
	const { shift, filters, list, selection } = steps;
	const selectedStaff = selection.selectedNurses.map((nurse) => ({ id: nurse.id, name: formatPersonName(nurse) }));
	const selectedIds = selection.selectedNurses.map((nurse) => nurse.id);
	const costLabel = useSelectionCostLabel(props.selectionMode === "multiple", list.rows, selectedIds);
	if (!shift.window) return null;
	const summary = buildAddStaffSummary({
		selectedNames: selectedStaff.map((staff) => staff.name),
		staffingDate: shift.staffingDate,
		window: shift.window,
		location: filters.selectedRoom,
		showLocation: !props.hideRoomSelect,
		t,
	});
	return {
		detail,
		onDetailChange: setDetail,
		summary,
		shiftIndex: getShiftIndexForWindow(shift.window),
		hasSelection: selectedStaff.length > 0,
		selectedStaff,
		costLabel,
		confirmLabel: confirmButtonLabel(selectedStaff.length, t),
		canSubmit: submit.canSubmit,
		confirmLoading: submit.isSubmitting || submit.isAwaitingCost,
		onCancel: close,
		onConfirm: submit.requestAdd,
	};
};

/** Step 2's props, straight from the steps' state. */
const toStaffStepProps = (
	props: ResolvedAddNurseProps,
	dayNo: number,
	{ shift, list, selection, filters }: AddStaffSteps,
): ComponentProps<typeof StaffStep> => ({
	window: shift.window,
	staffingDate: shift.staffingDate,
	rows: list.rows,
	availableCount: list.availableCount,
	dateRange: list.dateRange,
	dayNo,
	selectionMode: props.selectionMode,
	selectedRowIds: selection.selectedRowIds,
	onSelectionChange: selection.onSelectionChange,
	loading: list.isLoadingPredictions,
	contractTypes: filters.contractTypes,
	onContractTypesChange: filters.changeContractTypes,
	scope: list.scope,
	hasAnyHolder: list.hasAnyHolder,
});

/** The panel's title row: "Add staff" and the day it adds to — a date picker when the tab may move days. */
const AddStaffHeader: FC<{ date: AddStaffDate; shiftNo: number }> = ({ date, shiftNo }) => {
	const { t } = useTranslation();
	const { hospitalData } = useHospitalData();
	const dayLabel = formatShiftDate(hospitalData.weeks[date.weekIndex].weeklyShifts[shiftNo].days[date.dayNo].date);
	return (
		<NursePanelHeader
			weekIndex={date.weekIndex}
			dayNo={date.dayNo}
			shiftNo={shiftNo}
			isMasterSchedule={false}
			title={t("add.staff.title")}
			titleCss={HEADER_SX}
			changeDate={date.canChangeDate}
			onSelectedDateChanged={date.changeDate}
			minSelectableDate={date.minDate}
			dateAriaLabel={t("add.staff.date.change", { date: dayLabel })}
			dateDataTestId="add-nurse-date-button"
		/>
	);
};

/**
 * Add Staff: two steps — "Which shift?" (cards, then job/role and location) then "Who can work it?" (the
 * staff picker for exactly those hours, with the staff who can't work them folded away with a reason) — and
 * a footer that reads the add back before doing it. The header's date can re-target the whole tab.
 */
export const AddNurse = (rawProps: AddNurseProps) => {
	const { props, date, steps, submit, footer } = useAddStaffPanel(rawProps);
	const { filters, shift, selection } = steps;
	const focusNurse = selection.selectedNurses[0] ?? null;

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: STAFF_PICKER.PANEL_HEIGHT, overflow: "hidden" }} data-testid="add-nurse-panel">
			<AddStaffHeader date={date} shiftNo={props.shiftNo} />
			<Box sx={BODY_SX}>
				<ShiftStep
					shift={shift}
					rolesToDisplay={props.rolesToDisplay}
					requestJobId={props.requestJobId}
					selectedRoom={filters.selectedRoom}
					onRoomChange={(location, meta) => filters.changeRoom(location, meta, focusNurse?.id)}
					hideRoomSelect={props.hideRoomSelect}
					focusNurse={focusNurse}
					weekIndex={date.weekIndex}
					dayNo={date.dayNo}
					showActual={props.showActual}
				/>
				<Divider />
				<StaffStep {...toStaffStepProps(props, date.dayNo, steps)} />
			</Box>
			{footer && <AddStaffFooter {...footer} />}
			<AddStaffDialogs {...submit.dialogs} />
		</Box>
	);
};

/**
 * "Adds $480 this week" — the picks' summed cost change, for wage viewers in multi-select (single-select
 * rows already show their own figure). Hidden when any pick has no prediction yet.
 */
const useSelectionCostLabel = (isMultiple: boolean, rows: readonly NurseData[], selectedIds: readonly number[]): string | null => {
	const { t } = useTranslation();
	const canViewWage = useRevealWage();
	if (!isMultiple || !canViewWage) return null;
	const total = sumSelectedCost(rows, selectedIds);
	return total === null ? null : t("add.staff.summary.cost", { cost: formatPredictedCost(total) });
};
